import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeENGB from '@angular/common/locales/en-GB';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthInterceptor } from './interceptors/http.interceptor';
import { JwtModule } from '@auth0/angular-jwt';
import { environment } from '../environments/environment';
import { TemplateTranslationService } from './services/template-translation.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Helpers } from './shared/components/helpers';
import { ToastrModule } from 'ngx-toastr';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { MsalInterceptor, MsalModule, MsalRedirectComponent } from '@azure/msal-angular';
import { providePrimeNG } from 'primeng/config';
import { provideNativeDateAdapter } from '@angular/material/core';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { palette } from '@primeng/themes';
import { provideHighcharts } from 'highcharts-angular';

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: palette('#30619F') as any,
  }
});

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'https://api.kiwaimpact.com/v2/translations/?lang=', '');
}

registerLocaleData(localeENGB);

@NgModule({ declarations: [AppComponent],
  bootstrap: [AppComponent, MsalRedirectComponent], imports: [BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    JwtModule.forRoot({
      config: {
        tokenGetter: (request) => {
          if (request.headers.has('no-token')) {
            return null;
          }
          return localStorage.getItem('impact-token');
          },
        allowedDomains: environment.allowedDomains,
        disallowedRoutes: [/no-token/],
      }
    }),
    ToastrModule.forRoot(),
    MsalModule.forRoot(new PublicClientApplication({
      auth: {
        clientId: environment.oauthData.microsoftOauth.clientId,
        redirectUri: environment.oauthData.microsoftOauth.redirectUri,
        postLogoutRedirectUri: environment.oauthData.microsoftOauth.logoutRedirectUri,
        navigateToLoginRequestUrl: false
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
        temporaryCacheLocation: 'sessionStorage'
      },
    }), {
      interactionType: InteractionType.Redirect,
      authRequest: {
        scopes: ['user.read'],
      }
      }, {
      interactionType: InteractionType.Redirect,
      protectedResourceMap: new Map([
        ['https://graph.microsoft.com/v1.0/me', ['user.read']],
      ]),
    })], providers: [
      TemplateTranslationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    TranslateService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    TranslateService,
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: 'none'
        }
      }
    }),
    provideNativeDateAdapter(),
    provideHttpClient(withInterceptorsFromDi()),
    provideHighcharts({
      instance: () => import('highcharts/esm/highcharts').then(m => m.default),
      modules: () => {
        return [
          import('highcharts/esm/modules/accessibility'),
          import('highcharts/esm/modules/exporting'),
        ];
      }
    })
  ]
})
export class AppModule {
  constructor(translate: TranslateService) {
    translate.setDefaultLang('en');
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.language) {
      translate.use(user.language.code);
      Helpers.changeLocale(user.language.code);
    }
  }
}
